/**
 * Requisitions: a branch ordering from the group's own central kitchen or store.
 *
 * Three things a screen built on this has to get right.
 *
 * **Every stage keeps its own figure.** A line carries `requested`, `approved`,
 * `dispatched` and `received`, all in the item's stock unit, and each is null
 * until that stage has happened. Show the one that matters for the stage the
 * requisition is at; never collapse them into one "quantity" column, because
 * the differences are the whole story.
 *
 * **`value` is `string | null` on a line, and null means unknown, not free.**
 * An item nobody has ever put a cost on has no value, and the requisition's
 * `unpriced` counts such lines so its total is never quietly short.
 *
 * **A spend-limit refusal is a 409 with the numbers in it.** `over_limit`
 * lists each limit, what it is and what this would take it to, and
 * `may_override` says whether the person may raise it anyway — in which case
 * the screen asks, and sends the same request again with `overLimit: true`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from './client';
import { useApi } from './provider';

export type RequisitionState =
  'submitted' | 'accepted' | 'refused' | 'cancelled' | 'dispatched' | 'received';

/** What happened, in the words of the person waiting for the van. */
export const REQUISITION_STATE_LABELS: Record<RequisitionState, string> = {
  submitted: 'Waiting for an answer',
  accepted: 'Agreed',
  refused: 'Refused',
  cancelled: 'Called off',
  dispatched: 'On its way',
  received: 'Received',
};

export type LineDecision = 'accepted' | 'changed' | 'refused';

export interface Delivery {
  /** The day the van comes. */
  day: string;
  /** When orders for it close, with the supplying site's offset. */
  cutoff_at: string;
  /** The earlier van an order raised now has already missed, if any. */
  missed: string | null;
}

export interface SupplyingSite {
  id: string;
  location_id: string;
  location_name: string;
  storage_id: string;
  is_active: boolean;
  /** "14:00:00". Null means no calendar: orders go as soon as they can. */
  cutoff_time: string | null;
  /** ISO weekdays, Monday 1 to Sunday 7. Null is every day. */
  delivery_days: number[] | null;
  lead_days: number;
  /** Where an order raised right now would land. Null without a calendar. */
  next_delivery: Delivery | null;
}

export interface RequisitionLine {
  id: string;
  stock_item_id: string;
  sku: string;
  name: string;
  stock_unit: string;
  category: string | null;
  /** As typed, in `unit_code` — `ea` is one of the item's packs. */
  quantity: string;
  unit_code: string;
  /** Everything from here on is in `stock_unit`. */
  requested: string;
  unit_cost: string | null;
  /** Null is unknown, not free. */
  value: string | null;
  decision: LineDecision | null;
  approved: string | null;
  decision_reason: string | null;
  dispatched: string | null;
  dispatched_value: string | null;
  received: string | null;
  damaged: string | null;
  short: string | null;
  variance_value: string | null;
}

export interface DispatchNote {
  dispatched_at: string;
  dispatched_by: string | null;
  note: string | null;
  value: string;
  received_at: string | null;
  received_by: string | null;
  receive_note: string | null;
  variance_value: string | null;
}

export interface Requisition {
  id: string;
  number: number;
  location_id: string;
  location_name: string;
  storage_id: string;
  supplying_site_id: string;
  supplying_location_id: string;
  supplying_location_name: string;
  state: RequisitionState;
  note: string | null;
  raised_on: string;
  raised_at: string;
  raised_by: string | null;
  /** Null where the supplying site keeps no calendar. */
  delivery_date: string | null;
  /** Raised after the cut-off for an earlier van — why the date is later. */
  missed_cutoff: boolean;
  /** Somebody allowed to said yes over a spend limit. */
  over_limit: boolean;
  value: string;
  /** Lines with no known cost, left out of `value`. */
  unpriced: number;
  reviewed_at: string | null;
  closed_at: string | null;
  dispatch: DispatchNote | null;
  lines: RequisitionLine[];
}

export interface OrderGuideLine {
  stock_item_id: string;
  sku: string;
  name: string;
  stock_unit: string;
  par: string;
}

export interface OrderGuide {
  id: string;
  location_id: string;
  supplying_site_id: string;
  name: string;
  is_active: boolean;
  lines: OrderGuideLine[];
}

export interface GuideSuggestion {
  stock_item_id: string;
  sku: string;
  name: string;
  stock_unit: string;
  par: string;
  on_hand: string;
  on_order: string;
  shortfall: string;
  /** In `unit_code`: whole packs (`ea`) where the item comes in packs. */
  quantity: string;
  unit_code: string;
  /** One pack, in `stock_unit`, where the suggestion is in packs. */
  pack_size: string | null;
}

export type SpendPeriod = 'week' | 'month';

export interface SpendLimit {
  id: string;
  user_id: string | null;
  location_id: string | null;
  category: string | null;
  period: SpendPeriod;
  amount: string;
  is_active: boolean;
  period_start: string;
  period_end: string;
  /** Requisitioned against it so far this period. */
  spent: string;
}

export interface OverLimit {
  limit_id: string;
  /** In words: "Camden's spend on Dairy this week". */
  what: string;
  amount: string;
  would_be: string;
}

/**
 * The spend-limit refusal inside an error, if that is what it is.
 *
 * Read structurally, so a screen can offer "Raise it anyway" to somebody
 * allowed to, and show everyone else the limit and the figure.
 */
export function overLimitOf(
  error: unknown,
): { limits: OverLimit[]; mayOverride: boolean; message: string } | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  if (error.body.reason !== 'over_limit') return null;
  const limits = Array.isArray(error.body.over_limit)
    ? (error.body.over_limit as OverLimit[])
    : [];
  return { limits, mayOverride: error.body.may_override === true, message: error.message };
}

function invalidate(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ['requisitions'] });
}

function invalidateStock(queryClient: ReturnType<typeof useQueryClient>) {
  invalidate(queryClient);
  // Sending and receiving move stock, so any level on screen is stale.
  void queryClient.invalidateQueries({ queryKey: ['inventory'] });
}

// --------------------------------------------------------------------------- //
// Supplying sites
// --------------------------------------------------------------------------- //

export function useSupplyingSites(includeInactive = false) {
  const api = useApi();
  return useQuery({
    queryKey: ['requisitions', 'sites', includeInactive],
    queryFn: ({ signal }) =>
      api.get<SupplyingSite[]>(
        `/v1/requisitions/supplying-sites?include_inactive=${includeInactive}`,
        signal,
      ),
    // `next_delivery` is worked out for now, and a cut-off passes while a
    // screen is open.
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
}

export function useAddSupplyingSite() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<SupplyingSite, ApiError, { locationId: string; storageId: string }>({
    mutationFn: ({ locationId, storageId }) =>
      api.post<SupplyingSite>('/v1/requisitions/supplying-sites', {
        location_id: locationId,
        storage_id: storageId,
      }),
    onSettled: () => invalidate(queryClient),
  });
}

export function useUpdateSupplyingSite() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    SupplyingSite,
    ApiError,
    { siteId: string; storageId?: string; isActive?: boolean }
  >({
    mutationFn: ({ siteId, storageId, isActive }) =>
      api.request<SupplyingSite>(`/v1/requisitions/supplying-sites/${siteId}`, {
        method: 'PATCH',
        body: {
          storage_id: storageId ?? null,
          is_active: isActive ?? null,
        },
      }),
    onSettled: () => invalidate(queryClient),
  });
}

export interface ScheduleInput {
  siteId: string;
  /** "14:00", or null to clear the calendar. */
  cutoffTime: string | null;
  deliveryDays: number[];
  leadDays: number;
}

export function useSetSchedule() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<SupplyingSite, ApiError, ScheduleInput>({
    mutationFn: ({ siteId, cutoffTime, deliveryDays, leadDays }) =>
      api.request<SupplyingSite>(`/v1/requisitions/supplying-sites/${siteId}/schedule`, {
        method: 'PUT',
        body: {
          cutoff_time: cutoffTime,
          delivery_days: deliveryDays.length > 0 ? deliveryDays : null,
          lead_days: leadDays,
        },
      }),
    onSettled: () => invalidate(queryClient),
  });
}

// --------------------------------------------------------------------------- //
// Requisitions
// --------------------------------------------------------------------------- //

/** A branch's own requisitions, or a supplying site's incoming ones. */
export function useRequisitions(
  side: 'branch' | 'supplier',
  locationId: string | undefined,
  state?: RequisitionState,
) {
  const api = useApi();
  const key = side === 'branch' ? 'location_id' : 'supplying_location_id';
  return useQuery({
    queryKey: ['requisitions', 'list', side, locationId ?? null, state ?? null],
    queryFn: ({ signal }) =>
      api.get<Requisition[]>(
        `/v1/requisitions?${key}=${locationId}${state ? `&state=${state}` : ''}`,
        signal,
      ),
    enabled: Boolean(locationId),
    staleTime: 15_000,
  });
}

export function useRequisition(requisitionId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['requisitions', 'one', requisitionId ?? null],
    queryFn: ({ signal }) => api.get<Requisition>(`/v1/requisitions/${requisitionId}`, signal),
    enabled: Boolean(requisitionId),
    staleTime: 15_000,
  });
}

export interface RaiseLine {
  stockItemId: string;
  quantity: string;
  /** Null or absent is the stock unit; `ea` is one pack. */
  unitCode?: string | null;
}

export interface RaiseInput {
  locationId: string;
  supplyingSiteId: string;
  storageId: string;
  lines: RaiseLine[];
  note?: string;
  /** Only after a refusal that said `may_override`. See the note at the top. */
  overLimit?: boolean;
}

export function useRaiseRequisition() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Requisition, ApiError, RaiseInput>({
    mutationFn: (input) =>
      api.post<Requisition>('/v1/requisitions', {
        location_id: input.locationId,
        supplying_site_id: input.supplyingSiteId,
        storage_id: input.storageId,
        lines: input.lines.map((line) => ({
          stock_item_id: line.stockItemId,
          quantity: line.quantity,
          unit_code: line.unitCode ?? null,
        })),
        note: input.note ?? null,
        over_limit: input.overLimit ?? false,
      }),
    onSettled: () => invalidate(queryClient),
  });
}

export function useCancelRequisition() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Requisition, ApiError, { requisitionId: string }>({
    mutationFn: ({ requisitionId }) =>
      api.post<Requisition>(`/v1/requisitions/${requisitionId}/cancel`),
    onSettled: () => invalidate(queryClient),
  });
}

export interface DecisionInput {
  lineId: string;
  decision: LineDecision;
  /** Stock units. Required on a change. */
  quantity?: string;
  /** Required on a change or a refusal — the branch reads it. */
  reason?: string;
}

/** The supplying site's answer. Lines not named are accepted as asked. */
export function useReviewRequisition() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Requisition, ApiError, { requisitionId: string; lines: DecisionInput[] }>({
    mutationFn: ({ requisitionId, lines }) =>
      api.post<Requisition>(`/v1/requisitions/${requisitionId}/review`, {
        lines: lines.map((line) => ({
          line_id: line.lineId,
          decision: line.decision,
          quantity: line.quantity ?? null,
          reason: line.reason ?? null,
        })),
      }),
    onSettled: () => invalidate(queryClient),
  });
}

/**
 * Send it. Stock leaves the supplying site's shelf now, at its cost.
 *
 * `lines` names only the ones sending less than was agreed; everything else
 * goes as agreed. More than agreed is refused.
 */
export function useDispatchRequisition() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    Requisition,
    ApiError,
    { requisitionId: string; lines: { lineId: string; quantity: string }[]; note?: string }
  >({
    mutationFn: ({ requisitionId, lines, note }) =>
      api.post<Requisition>(`/v1/requisitions/${requisitionId}/dispatch`, {
        lines: lines.map((line) => ({ line_id: line.lineId, quantity: line.quantity })),
        note: note ?? null,
      }),
    onSettled: () => invalidateStock(queryClient),
  });
}

/**
 * Sign for the van. Lines not named arrived in full.
 *
 * Whatever is short or damaged is written off as waste at the branch with the
 * requisition's number on it, and the variance goes on the audit log.
 */
export function useReceiveRequisition() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    Requisition,
    ApiError,
    {
      requisitionId: string;
      lines: { lineId: string; received: string; damaged: string }[];
      note?: string;
    }
  >({
    mutationFn: ({ requisitionId, lines, note }) =>
      api.post<Requisition>(`/v1/requisitions/${requisitionId}/receive`, {
        lines: lines.map((line) => ({
          line_id: line.lineId,
          received: line.received,
          damaged: line.damaged,
        })),
        note: note ?? null,
      }),
    onSettled: () => invalidateStock(queryClient),
  });
}

// --------------------------------------------------------------------------- //
// Order guides
// --------------------------------------------------------------------------- //

export function useOrderGuides(locationId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['requisitions', 'guides', locationId ?? null],
    queryFn: ({ signal }) =>
      api.get<OrderGuide[]>(`/v1/requisitions/order-guides?location_id=${locationId}`, signal),
    enabled: Boolean(locationId),
    staleTime: 60_000,
  });
}

export function useCreateOrderGuide() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    OrderGuide,
    ApiError,
    { locationId: string; supplyingSiteId: string; name: string }
  >({
    mutationFn: ({ locationId, supplyingSiteId, name }) =>
      api.post<OrderGuide>('/v1/requisitions/order-guides', {
        location_id: locationId,
        supplying_site_id: supplyingSiteId,
        name,
      }),
    onSettled: () => invalidate(queryClient),
  });
}

export function useUpdateOrderGuide() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    OrderGuide,
    ApiError,
    { guideId: string; name?: string; isActive?: boolean }
  >({
    mutationFn: ({ guideId, name, isActive }) =>
      api.request<OrderGuide>(`/v1/requisitions/order-guides/${guideId}`, {
        method: 'PATCH',
        body: {
          name: name ?? null,
          is_active: isActive ?? null,
        },
      }),
    onSettled: () => invalidate(queryClient),
  });
}

/** Replace the guide's items and pars with the list as it is on screen. */
export function useSetOrderGuideLines() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    OrderGuide,
    ApiError,
    { guideId: string; lines: { stockItemId: string; par: string }[] }
  >({
    mutationFn: ({ guideId, lines }) =>
      api.request<OrderGuide>(`/v1/requisitions/order-guides/${guideId}/lines`, {
        method: 'PUT',
        body: {
          lines: lines.map((line) => ({ stock_item_id: line.stockItemId, par: line.par })),
        },
      }),
    onSettled: () => invalidate(queryClient),
  });
}

/**
 * What to ask for to bring the branch up to par.
 *
 * Fetched when somebody asks rather than kept warm: it reads every storage at
 * the branch and every open requisition, and it is only right at the moment
 * it is read.
 */
export function useGuideSuggestions(guideId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['requisitions', 'suggest', guideId ?? null],
    queryFn: ({ signal }) =>
      api.get<GuideSuggestion[]>(`/v1/requisitions/order-guides/${guideId}/suggest`, signal),
    enabled: Boolean(guideId),
    staleTime: 0,
  });
}

// --------------------------------------------------------------------------- //
// Spend limits
// --------------------------------------------------------------------------- //

export function useSpendLimits(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['requisitions', 'limits'],
    queryFn: ({ signal }) => api.get<SpendLimit[]>('/v1/requisitions/spend-limits', signal),
    enabled,
    staleTime: 60_000,
  });
}

export interface SpendLimitInput {
  /** Exactly one of the two. */
  userId?: string | null;
  locationId?: string | null;
  category?: string | null;
  period: SpendPeriod;
  amount: string;
}

export function useAddSpendLimit() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<SpendLimit, ApiError, SpendLimitInput>({
    mutationFn: (input) =>
      api.post<SpendLimit>('/v1/requisitions/spend-limits', {
        user_id: input.userId ?? null,
        location_id: input.locationId ?? null,
        category: input.category ?? null,
        period: input.period,
        amount: input.amount,
      }),
    onSettled: () => invalidate(queryClient),
  });
}

export function useUpdateSpendLimit() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    SpendLimit,
    ApiError,
    { limitId: string; amount?: string; isActive?: boolean }
  >({
    mutationFn: ({ limitId, amount, isActive }) =>
      api.request<SpendLimit>(`/v1/requisitions/spend-limits/${limitId}`, {
        method: 'PATCH',
        body: {
          amount: amount ?? null,
          is_active: isActive ?? null,
        },
      }),
    onSettled: () => invalidate(queryClient),
  });
}
